/**
 * 监听 window scroll
 * @param callback
 * @param depend 依赖
 */
import { useCallback, useEffect, useRef } from 'react';
import { $window, docElement } from '@umu/umu-dom';
import { throttle, throttleGuarantee } from '@umu/umu-util';

/**
 * 监听滚动
 * @param callback 回调
 * @param depend 依赖
 * @param action 启动或取消监听
 * @author Kody
 */
export const useListenScroll = (callback: (distance: number) => void, depend: any[] = [], action = true) => {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;
    let timer: number = 0;
    let scrollTop = docElement.scrollTop;
    let startTop = docElement.scrollTop;

    const scroll = throttle(() => {
        if (timer) {
            $window.clearTimeout(timer);
        }
        scrollTop = docElement.scrollTop;
        timer = $window.setTimeout(() => {
            const newScrollTop = docElement.scrollTop;
            if (scrollTop === newScrollTop) {
                startTop = scrollTop;
            }
        }, 300);
        callbackRef.current(docElement.scrollTop - startTop);
    }, 100);
    useEffect(() => {
        if (action) {
            $window.addEventListener('scroll', scroll);
        }
        return () => {
            $window.removeEventListener('scroll', scroll);
        };
    }, [action, ...depend]);
};

interface IOption {
    viewer: HTMLElement | undefined;
    action?: boolean;
}
export const useScroll = (callback: (distance: number, scrollTop: number) => void, option?: IOption) => {
    const viewer = option?.viewer;
    const action = option?.action ?? true;
    const callbackRef = useRef(callback);
    callbackRef.current = callback;
    const timer = useRef(0);
    const $ele = viewer || docElement;
    const scrollTop = useRef($ele.scrollTop);
    const startTop = useRef($ele.scrollTop);

    const scroll = useCallback(
        throttleGuarantee(() => {
            if (timer.current) {
                $window.clearTimeout(timer.current);
            }
            scrollTop.current = $ele.scrollTop;
            timer.current = $window.setTimeout(() => {
                const newScrollTop = $ele.scrollTop;
                if (scrollTop.current === newScrollTop) {
                    startTop.current = scrollTop.current;
                }
            }, 200);
            callbackRef.current($ele.scrollTop - startTop.current, $ele.scrollTop);
        }, 100),
        [$ele]
    );

    useEffect(() => {
        if (action) {
            (viewer || $window).addEventListener('scroll', scroll);
        }
        return () => {
            (viewer || $window).removeEventListener('scroll', scroll);
        };
    }, [viewer, action, scroll]);
};
